/* eslint-disable max-len */
import { pad } from './string-utils';

import { LONG_MONTH_NAMES, SHORT_MONTH_NAMES } from './date';
import { has } from './object';

const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const LONG_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TOKEN_REGEX = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;
const TIMEZONE_REGEX =
    /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g;
const TIMEZONE_CLIP_REGEX = /[^\-+\dA-Z]/g;

type DateInput = number | string | Date;

// A formatter takes a date-like value (and optional utc flag) and returns a formatted string.
export type DateFormatterFn = (val: DateInput, utc?: boolean) => string;

/*
 * Date Formatting Function
 * Taken From: http://blog.stevenlevithan.com/archives/date-time-format
 * Simplified a bit to get rid of some error protection and alternate call styles
 * this we don't need.
 */
function formatDate(inputDate: DateInput, mask: string, utc: boolean): string {
    // Regexes and supporting functions are cached through closure
    // Passing date through Date applies Date.parse, if necessary
    const date = inputDate ? new Date(inputDate) : new Date();
    if (Number.isNaN(date.getTime())) throw SyntaxError('invalid date');

    const d = utc ? date.getUTCDate() : date.getDate();
    const D = utc ? date.getUTCDay() : date.getDay();
    const m = utc ? date.getUTCMonth() : date.getMonth();
    const y = utc ? date.getUTCFullYear() : date.getFullYear();
    const H = utc ? date.getUTCHours() : date.getHours();
    const M = utc ? date.getUTCMinutes() : date.getMinutes();
    const s = utc ? date.getUTCSeconds() : date.getSeconds();
    const L = utc ? date.getUTCMilliseconds() : date.getMilliseconds();
    const o = utc ? 0 : date.getTimezoneOffset();
    const flags: Record<string, string | number> = {
        d,
        dd: pad(d),
        ddd: SHORT_DAY_NAMES[D],
        dddd: LONG_DAY_NAMES[D],
        m: m + 1,
        mm: pad(m + 1),
        mmm: SHORT_MONTH_NAMES[m],
        mmmm: LONG_MONTH_NAMES[m],
        yy: String(y).slice(2),
        yyyy: y,
        h: H % 12 || 12,
        hh: pad(H % 12 || 12),
        H,
        HH: pad(H),
        M,
        MM: pad(M),
        s,
        ss: pad(s),
        l: pad(L, 3),
        L: pad(L > 99 ? Math.round(L / 10) : L),
        t: H < 12 ? 'a' : 'p',
        tt: H < 12 ? 'am' : 'pm',
        T: H < 12 ? 'A' : 'P',
        TT: H < 12 ? 'AM' : 'PM',
        Z: utc ? 'UTC' : ((String(date).match(TIMEZONE_REGEX) || ['']).pop() ?? '').replace(TIMEZONE_CLIP_REGEX, ''),
        o: (o > 0 ? '-' : '+') + pad(Math.floor((Math.abs(o) / 60) * 100) + (Math.abs(o) % 60), 4),
        S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : Number((d % 100) - (d % 10) !== 10) * (d % 10)],
    };

    return mask.replace(TOKEN_REGEX, ($0) => {
        return has(flags, $0) ? String(flags[$0]) : $0.slice(1, $0.length - 1);
    });
}

function monthDayTimeFormatter(val: DateInput, utc = true): string {
    return formatDate(val, 'mmm d HH:MM', utc);
}

function monthDayYearFormatter(val: DateInput, utc = true): string {
    return formatDate(val, "mmm d 'yy", utc);
}

function monthYearFormatter(val: DateInput, utc = true): string {
    return formatDate(val, "mmm 'yy", utc);
}

function quarterFormatter(val: DateInput, utc = true): string {
    // JS month is 0 indexed so we adjust
    const d = new Date(val);
    const month = d.getUTCMonth() + 1;
    let year = d.getUTCFullYear();
    let quarter;
    quarter = '';
    switch (month) {
        case 2:
        case 3:
        case 4:
            quarter = 'Q1';
            break;
        case 5:
        case 6:
        case 7:
            quarter = 'Q2';
            break;
        case 8:
        case 9:
        case 10:
            quarter = 'Q3';
            break;
        case 11:
        case 12:
        case 1:
            quarter = 'Q4';
            if (month === 1) {
                year -= 1;
            }
            break;
        default:
            break;
    }

    const yearDate = new Date(Date.UTC(year, 0));
    const formattedYear = formatDate(yearDate, "'yy", utc);
    return `${quarter} ${formattedYear}`;
}

function yearFormatter(val: DateInput, utc = true): string {
    return formatDate(val, 'yyyy', utc);
}

function getFormatter(type: string, quarterlyFormat?: string): DateFormatterFn | null {
    switch (type) {
        case 'intraday':
            return monthDayTimeFormatter;
        case 'daily':
        case 'market_daily':
        case 'weekly':
        case 'biweekly':
            return monthDayYearFormatter;
        case 'monthly':
        case 'bimonthly':
            return monthYearFormatter;
        case 'quarterly':
        case 'semi_yearly':
            if (quarterlyFormat === 'month') return monthYearFormatter;
            return quarterFormatter;
        case 'yearly':
        case 'fiscal_yearly':
            return yearFormatter;
        default:
            return null;
    }
}

export default {
    formatDate,
    getFormatter,
};
